import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, StepContent,
  Typography, Chip, LinearProgress, Button, Box,
} from '@mui/material';
import MemberLocation, { MemberLocationFields } from '../common/MemberLocation';

interface MemberPos extends MemberLocationFields {
  user_id: string; name: string | null; email: string | null;
  current_phase: number; current_milestone: number; milestone_label: string;
  hours_idle: number | null; eligible_funnel_key: string; eligible_funnel_phase: string;
  is_subscriber: boolean; conversion_score: number | null; engagement_tier: string | null;
  enrolled_in_funnel: boolean;
}

interface Props {
  open: boolean;
  member: MemberPos | null;
  onClose: () => void;
}

const PHASE_NAMES = [
  'Acquisition & Account',
  'First Free Chapter',
  // "(legacy)": the free grant has been ONE Chapter since 2026-08-18; this
  // phase only appears in the history of grandfathered Members. The label is
  // suffixed so an operator reading such a journey isn't misled into thinking
  // a second free chapter is a live product state. (This dialog reflects the
  // DB view v_member_activation_position, which still models six phases.)
  'Second Free Chapter (legacy)',
  'Conversion',
  'First Subscriber Chapter',
  'Habit & Retention',
];

const TIER_COLOR: Record<string, { bg: string; fg: string }> = {
  hot:  { bg: '#F1E3E7', fg: '#7D1935' },
  warm: { bg: '#FBF3DA', fg: '#9A7B0A' },
  cold: { bg: '#EAEEF1', fg: '#5D6D7E' },
};

function idleChipColor(h: number | null): 'error' | 'warning' | 'success' | 'default' {
  if (h == null) return 'default';
  if (h > 48) return 'error';
  if (h > 24) return 'warning';
  return 'success';
}

export default function MemberJourneyDialog({ open, member, onClose }: Props) {
  if (!member) return null;

  // Clamped both ways: a phase id past the array rendered a Stepper with no
  // active step. The ids are the pipeline view's phases, not the funnel's.
  const activeStep = Math.min(PHASE_NAMES.length - 1, Math.max(0, (member.current_phase ?? 1) - 1));
  const score = member.conversion_score ?? 0;
  const tierColors = member.engagement_tier ? TIER_COLOR[member.engagement_tier] : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, pb: 1 }}>
        {member.name || member.email || 'Member'}'s Activation Journey
        <Typography sx={{ fontSize: 12, color: '#5D6D7E', fontFamily: 'inherit', fontWeight: 400, mt: 0.25 }}>
          {member.email}
        </Typography>
        <Box sx={{ mt: 0.25 }}>
          <MemberLocation
                      location_country_code={member.location_country_code}
                      location_region={member.location_region}
                      location_city={member.location_city}
                      location_source={member.location_source}
                      location_confidence={member.location_confidence}
                      location_resolved_at={member.location_resolved_at}
                    />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {PHASE_NAMES.map((name, i) => (
            <Step key={i} completed={i < activeStep}>
              <StepLabel
                optional={i === activeStep
                  ? <Typography sx={{ fontSize: 11, color: '#5D6D7E' }}>Current phase</Typography>
                  : null}
              >
                <Typography sx={{ fontSize: 13, fontWeight: i === activeStep ? 700 : 400 }}>
                  Phase {i + 1}: {name}
                </Typography>
              </StepLabel>
              {i === activeStep && (
                <StepContent>
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A2735', mb: 0.5 }}>
                      {member.milestone_label || `Milestone ${member.current_milestone}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#5D6D7E', mb: 1 }}>
                      {/* current_milestone is a config step ID (stable, gaps,
                          max 50), not an ordinal — "of 50" implied a remaining
                          count that doesn't exist. */}
                      Step {member.current_milestone}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      {member.hours_idle != null && (
                        <Chip
                          size="small"
                          label={`Idle ${Math.round(member.hours_idle)}h`}
                          color={idleChipColor(member.hours_idle)}
                          sx={{ fontSize: 11, fontWeight: 700, height: 22 }}
                        />
                      )}
                      {tierColors && member.engagement_tier && (
                        <Chip
                          size="small"
                          label={member.engagement_tier}
                          sx={{ fontSize: 11, fontWeight: 700, height: 22, textTransform: 'capitalize',
                            bgcolor: tierColors.bg, color: tierColors.fg }}
                        />
                      )}
                      {member.is_subscriber && (
                        <Chip size="small" label="Subscriber" color="primary"
                          sx={{ fontSize: 11, fontWeight: 700, height: 22 }} />
                      )}
                    </Box>
                  </Box>
                </StepContent>
              )}
            </Step>
          ))}
        </Stepper>

        {member.eligible_funnel_key && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #EAEEF1' }}>
            <Typography sx={{ fontSize: 12, color: '#5D6D7E', mb: 0.75 }}>Eligible for funnel:</Typography>
            <Chip
              label={member.eligible_funnel_key}
              size="small"
              sx={{ bgcolor: '#E8F4F8', color: '#1A5F7A', fontWeight: 700, fontSize: 12 }}
            />
          </Box>
        )}

        <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #EAEEF1' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontSize: 12, color: '#5D6D7E' }}>Conversion score</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1A2735' }}>{score}</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, score)}
            sx={{
              height: 6, borderRadius: 3, bgcolor: '#EAEEF1',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: score >= 70 ? '#27AE60' : score >= 40 ? '#F39C12' : '#E74C3C',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
